var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Basecoat } from '../common';
import { Point, Rectangle } from '../geometry';
import { Cell } from '../model/cell';
import { Node } from '../model/node';
import { Edge } from '../model/edge';
import { CellView } from '../view/cell';
import { HTML } from '../shape/standard/html';
import { Base } from './base';
import { GraphView } from './view';
import { Decorator } from './decorator';
import { Hook as HookManager } from './hook';
import { Options as GraphOptions } from './options';
import { DefsManager as Defs } from './defs';
import { GridManager as Grid } from './grid';
import { CoordManager as Coord } from './coord';
import { Keyboard as Shortcut } from './keyboard';
import { PrintManager as Print } from './print';
import { MouseWheel as Wheel } from './mousewheel';
import { FormatManager as Format } from './format';
import { Renderer as ViewRenderer } from './renderer';
import { HistoryManager as History } from './history';
import { MiniMapManager as MiniMap } from './minimap';
import { SnaplineManager as Snapline } from './snapline';
import { ScrollerManager as Scroller } from './scroller';
import { SelectionManager as Selection } from './selection';
import { HighlightManager as Highlight } from './highlight';
import { TransformManager as Transform } from './transform';
import { ClipboardManager as Clipboard } from './clipboard';
import { BackgroundManager as Background } from './background';
import { NodeTool, EdgeTool, PortLayout, PortLabelLayout, Attr as AttrDefinition, Grid as GridDefinition, Filter as FilterDefinition, Background as BackgroundDefinition, Highlighter as HighlightDefinition, Marker, Router, Connector, ConnectionPoint, ConnectionStrategy, NodeConnectionAnchor, EdgeConnectionAnchor, } from '../registry';
export class Graph extends Basecoat {
    constructor(options) {
        super();
        this.options = GraphOptions.merge(options);
        this.hook = new HookManager(this);
        this.view = this.hook.createView();
        this.defs = this.hook.createDefsManager();
        this.coord = this.hook.createCoordManager();
        this.transform = this.hook.createTransformManager();
        this.highlight = this.hook.createHighlightManager();
        this.grid = this.hook.createGridManager();
        this.background = this.hook.createBackgroundManager();
        this.model = this.hook.createModel();
        this.renderer = this.hook.createRenderer();
        this.clipboard = this.hook.createClipboardManager();
        this.snapline = this.hook.createSnaplineManager();
        this.selection = this.hook.createSelectionManager();
        this.history = this.hook.createHistoryManager();
        this.scroller = this.hook.createScrollerManager();
        this.minimap = this.hook.createMiniMapManager();
        this.keyboard = this.hook.createKeyboard();
        this.mousewheel = this.hook.createMouseWheel();
        this.print = this.hook.createPrintManager();
        this.format = this.hook.createFormatManager();
        this.setup();
    }
    get container() {
        return this.view.container;
    }
    setup() {
        this.model.on('sorted', () => this.trigger('model:sorted'));
        this.model.on('reseted', (args) => this.trigger('model:reseted', args));
        this.model.on('updated', (args) => this.trigger('model:updated', args));
    }
    // #region model
    resetModel(cells, options = {}) {
        this.model.resetCells(cells, options);
        return this;
    }
    clearModel(options = {}) {
        this.model.clear(options);
        return this;
    }
    toJSON(options = {}) {
        return this.model.toJSON(options);
    }
    parseJSON(data) {
        return this.model.parseJSON(data);
    }
    fromJSON(data, options = {}) {
        this.model.fromJSON(data, options);
        return this;
    }
    getCellById(id) {
        return this.model.getCell(id);
    }
    addNode(node, options = {}) {
        return this.model.addNode(node, options);
    }
    createNode(metadata) {
        return this.model.createNode(metadata);
    }
    addEdge(edge, options = {}) {
        return this.model.addEdge(edge, options);
    }
    createEdge(metadata) {
        return this.model.createEdge(metadata);
    }
    addCell(cell, options = {}) {
        this.model.addCell(cell, options);
        return this;
    }
    removeCell(cell, options = {}) {
        return this.model.removeCell(cell, options);
    }
    removeCells(cells, options = {}) {
        return this.model.removeCells(cells, options);
    }
    removeConnectedEdges(cell, options = {}) {
        return this.model.removeConnectedEdges(cell, options);
    }
    disconnectEdges(cell, options) {
        this.model.disconnectEdges(cell, options);
        return this;
    }
    hasCell(cell) {
        return this.model.has(cell);
    }
    getCell(id) {
        return this.model.getCell(id);
    }
    getCells() {
        return this.model.getCells();
    }
    getCellCount() {
        return this.model.total();
    }
    /**
     * Returns all the nodes in the graph.
     */
    getNodes() {
        return this.model.getNodes();
    }
    /**
     * Returns all the edges in the graph.
     */
    getEdges() {
        return this.model.getEdges();
    }
    /**
     * Returns all outgoing edges for the node.
     */
    getOutgoingEdges(cell) {
        return this.model.getOutgoingEdges(cell);
    }
    /**
     * Returns all incoming edges for the node.
     */
    getIncomingEdges(cell) {
        return this.model.getIncomingEdges(cell);
    }
    /**
     * Returns edges connected with cell.
     */
    getConnectedEdges(cell, options = {}) {
        return this.model.getConnectedEdges(cell, options);
    }
    /**
     * Returns an array of all the roots of the graph.
     */
    getRootCells() {
        return this.model.getRoots();
    }
    /**
     * Returns an array of all the leafs of the graph.
     */
    getLeafCells() {
        return this.model.getLeafs();
    }
    /**
     * Returns `true` if the node is a root node, i.e.
     * there is no  edges coming to the node.
     */
    isOriginCell(cell) {
        return this.model.isOrigin(cell);
    }
    /**
     * Returns `true` if the node is a leaf node, i.e.
     * there is no edges going out from the node.
     */
    isLeafCell(cell) {
        return this.model.isLeaf(cell);
    }
    /**
     * Returns all the neighbors of node in the graph. Neighbors are all
     * the nodes connected to node via either incoming or outgoing edge.
     */
    getNeighbors(cell, options = {}) {
        return this.model.getNeighbors(cell, options);
    }
    /**
     * Returns `true` if `cell2` is a neighbor of `cell1`.
     */
    isNeighbor(cell1, cell2, options = {}) {
        return this.model.isNeighbor(cell1, cell2, options);
    }
    getSuccessors(cell, options) {
        return this.model.getSuccessors(cell, options);
    }
    /**
     * Returns `true` if `cell2` is a successor of `cell1`.
     */
    isSuccessor(cell1, cell2) {
        return this.model.isSuccessor(cell1, cell2);
    }
    getPredecessors(cell, options) {
        return this.model.getPredecessors(cell, options);
    }
    /**
     * Returns `true` if `cell2` is a predecessor of `cell1`.
     */
    isPredecessor(cell1, cell2) {
        return this.model.isPredecessor(cell1, cell2);
    }
    getCommonAncestor(...cells) {
        return this.model.getCommonAncestor(...cells);
    }
    /**
     * Returns an array of cells that result from finding nodes/edges that
     * are connected to any of the cells in the cells array. This function
     * loops over cells and if the current cell is a edge, it collects its
     * source/target nodes; if it is an node, it collects its incoming and
     * outgoing edges if both the edge terminal (source/target) are in the
     * cells array.
     */
    getSubGraph(cells, options = {}) {
        return this.model.getSubGraph(cells, options);
    }
    /**
     * Clones the whole subgraph (including all the connected links whose
     * source/target is in the subgraph). If `options.deep` is `true`, also
     * take into account all the embedded cells of all the subgraph cells.
     *
     * Returns a map of the form: { [original cell ID]: [clone] }.
     */
    cloneSubGraph(cells, options = {}) {
        return this.model.cloneSubGraph(cells, options);
    }
    cloneCells(cells) {
        return this.model.cloneCells(cells);
    }
    getNodesFromPoint(x, y) {
        return this.getNodesFromPoint(x, y);
    }
    getNodesInArea(x, y, w, h, options) {
        return this.model.getNodesInArea(x, y, w, h, options);
    }
    getNodesUnderNode(node, options = {}) {
        return this.model.getNodesUnderNode(node, options);
    }
    searchCell(cell, iterator, options = {}) {
        this.model.search(cell, iterator, options);
        return this;
    }
    /***
     * Returns an array of IDs of nodes on the shortest
     * path between source and target.
     */
    getShortestPath(source, target, options = {}) {
        return this.model.getShortestPath(source, target, options);
    }
    /**
     * Returns the bounding box that surrounds all cells in the graph.
     */
    getAllCellsBBox() {
        return this.model.getAllCellsBBox();
    }
    /**
     * Returns the bounding box that surrounds all the given cells.
     */
    getCellsBBox(cells, options = {}) {
        return this.model.getCellsBBox(cells, options);
    }
    startBatch(name, data = {}) {
        this.model.startBatch(name, data);
    }
    stopBatch(name, data = {}) {
        this.model.stopBatch(name, data);
    }
    batchUpdate(name, execute, data) {
        this.startBatch(name, data);
        const result = execute();
        this.stopBatch(name, data);
        return result;
    }
    //#endregion
    // #region cell
    isNode(cell) {
        return cell.isNode();
    }
    isEdge(cell) {
        return cell.isEdge();
    }
    // #endregion
    // #region node
    // #endregion
    // #region edge
    // #endregion
    // #region view
    isFrozen() {
        return this.renderer.isFrozen();
    }
    freeze(options = {}) {
        this.renderer.freeze(options);
        return this;
    }
    unfreeze(options = {}) {
        this.renderer.unfreeze(options);
        return this;
    }
    isAsync() {
        return this.renderer.isAsync();
    }
    findView(ref) {
        if (ref instanceof Cell) {
            return this.findViewByCell(ref);
        }
        return this.findViewByElem(ref);
    }
    findViews(ref) {
        if (Rectangle.isRectangleLike(ref)) {
            return this.findViewsInArea(ref);
        }
        if (Point.isPointLike(ref)) {
            return this.findViewsFromPoint(ref);
        }
        return [];
    }
    findViewByCell(cell) {
        return this.renderer.findViewByCell(cell);
    }
    findViewByElem(elem) {
        return this.renderer.findViewByElem(elem);
    }
    findViewsFromPoint(x, y) {
        const p = typeof x === 'number' ? { x, y: y } : x;
        return this.renderer.findViewsFromPoint(p);
    }
    findViewsInArea(x, y, width, height, options) {
        const rect = typeof x === 'number'
            ? {
                x,
                y: y,
                width: width,
                height: height,
            }
            : x;
        const localOptions = typeof x === 'number'
            ? options
            : y;
        return this.renderer.findViewsInArea(rect, localOptions);
    }
    isViewMounted(view) {
        return this.renderer.isViewMounted(view);
    }
    getMountedViews() {
        return this.renderer.getMountedViews();
    }
    getUnmountedViews() {
        return this.renderer.getUnmountedViews();
    }
    matrix(mat) {
        if (typeof mat === 'undefined') {
            return this.transform.getMatrix();
        }
        this.transform.setMatrix(mat);
        return this;
    }
    resize(width, height) {
        this.transform.resize(width, height);
        return this;
    }
    getScale() {
        return this.scale();
    }
    scale(sx, sy = sx, ox = 0, oy = 0) {
        if (typeof sx === 'undefined') {
            return this.transform.getScale();
        }
        this.transform.scale(sx, sy, ox, oy);
        return this;
    }
    rotate(angle, cx, cy) {
        if (typeof angle === 'undefined') {
            return this.transform.getRotation();
        }
        this.transform.rotate(angle, cx, cy);
        return this;
    }
    translate(tx, ty) {
        if (typeof tx === 'undefined') {
            return this.transform.getTranslation();
        }
        this.transform.translate(tx, ty);
        return this;
    }
    setOrigin(ox, oy) {
        return this.translate(ox || 0, oy || 0);
    }
    fitToContent(gridWidth, gridHeight, padding, options) {
        return this.transform.fitToContent(gridWidth, gridHeight, padding, options);
    }
    scaleContentToFit(options = {}) {
        this.transform.scaleContentToFit(options);
        return this;
    }
    getContentArea(options = {}) {
        return this.transform.getContentArea(options);
    }
    getContentBBox(options = {}) {
        return this.transform.getContentBBox(options);
    }
    getArea() {
        return this.transform.getArea();
    }
    getRestrictedArea(view) {
        return this.transform.getRestrictedArea(view);
    }
    // #endregion
    // #region coord
    getClientMatrix() {
        return this.coord.getClientMatrix();
    }
    /**
     * Returns coordinates of the graph viewport, relative to the window.
     */
    getClientOffset() {
        return this.coord.getClientOffset();
    }
    /**
     * Returns coordinates of the graph viewport, relative to the document.
     */
    getPageOffset() {
        return this.coord.getPageOffset();
    }
    snapToGrid(x, y) {
        return this.coord.snapToGrid(x, y);
    }
    localToGraphPoint(x, y) {
        return this.coord.localToGraphPoint(x, y);
    }
    localToClientPoint(x, y) {
        return this.coord.localToClientPoint(x, y);
    }
    localToPagePoint(x, y) {
        return this.coord.localToPagePoint(x, y);
    }
    localToGraphRect(x, y, width, height) {
        return this.coord.localToGraphRect(x, y, width, height);
    }
    localToClientRect(x, y, width, height) {
        return this.coord.localToClientRect(x, y, width, height);
    }
    localToPageRect(x, y, width, height) {
        return this.coord.localToPageRect(x, y, width, height);
    }
    graphToLocalPoint(x, y) {
        return this.coord.graphToLocalPoint(x, y);
    }
    clientToLocalPoint(x, y) {
        return this.coord.clientToLocalPoint(x, y);
    }
    pageToLocalPoint(x, y) {
        return this.coord.pageToLocalPoint(x, y);
    }
    graphToLocalRect(x, y, width, height) {
        return this.coord.graphToLocalRect(x, y, width, height);
    }
    clientToLocalRect(x, y, width, height) {
        return this.coord.clientToLocalRect(x, y, width, height);
    }
    pageToLocalRect(x, y, width, height) {
        return this.coord.pageToLocalRect(x, y, width, height);
    }
    // #endregion
    // #region defs
    defineFilter(options) {
        return this.defs.filter(options);
    }
    defineGradient(options) {
        return this.defs.gradient(options);
    }
    defineMarker(options) {
        return this.defs.marker(options);
    }
    // #endregion
    // #region grid
    getGridSize() {
        return this.grid.getGridSize();
    }
    setGridSize(gridSize) {
        this.grid.setGridSize(gridSize);
        return this;
    }
    showGrid() {
        this.grid.show();
        return this;
    }
    hideGrid() {
        this.grid.hide();
        return this;
    }
    clearGrid() {
        this.grid.clear();
        return this;
    }
    drawGrid(options) {
        this.grid.draw(options);
        return this;
    }
    // #endregion
    // #region background
    updateBackground() {
        this.background.update();
        return this;
    }
    drawBackground(options) {
        this.background.draw(options);
        return this;
    }
    clearBackground() {
        this.background.clear();
        return this;
    }
    // #endregion
    // #region clipboard
    isClipboardEnabled() {
        return !this.clipboard.disabled;
    }
    enableClipboard() {
        this.clipboard.enable();
        return this;
    }
    disableClipboard() {
        this.clipboard.disable();
        return this;
    }
    toggleClipboard(enabled) {
        if (enabled != null) {
            if (enabled !== this.isClipboardEnabled()) {
                if (enabled) {
                    this.enableClipboard();
                }
                else {
                    this.disableClipboard();
                }
            }
        }
        else {
            if (this.isClipboardEnabled()) {
                this.disableClipboard();
            }
            else {
                this.enableClipboard();
            }
        }
        return this;
    }
    isClipboardEmpty() {
        return this.clipboard.isEmpty();
    }
    getCellsInClipboard() {
        return this.clipboard.cells;
    }
    cleanClipboard() {
        this.clipboard.clean();
        return this;
    }
    copy(cells, options = {}) {
        this.clipboard.copy(cells, options);
        return this;
    }
    cut(cells, options = {}) {
        this.clipboard.cut(cells, options);
        return this;
    }
    paste(options = {}, graph = this) {
        return this.clipboard.paste(options, graph);
    }
    // #endregion
    // #region redo/undo
    isHistoryEnabled() {
        return !this.history.disabled;
    }
    enableHistory() {
        this.history.enable();
        return this;
    }
    disableHistory() {
        this.history.disable();
        return this;
    }
    toggleHistory(enabled) {
        if (enabled != null) {
            if (enabled !== this.isHistoryEnabled()) {
                if (enabled) {
                    this.enableHistory();
                }
                else {
                    this.disableHistory();
                }
            }
        }
        else {
            if (this.isHistoryEnabled()) {
                this.disableHistory();
            }
            else {
                this.enableHistory();
            }
        }
        return this;
    }
    undo(options = {}) {
        this.history.undo(options);
        return this;
    }
    undoAndCancel(options = {}) {
        this.history.cancel(options);
        return this;
    }
    redo(options = {}) {
        this.history.redo(options);
        return this;
    }
    canUndo() {
        return this.history.canUndo();
    }
    canRedo() {
        return this.history.canRedo();
    }
    cleanHistory(options = {}) {
        this.history.clean(options);
    }
    // #endregion
    // #region keyboard
    isKeyboardEnabled() {
        return !this.keyboard.disabled;
    }
    enableKeyboard() {
        this.keyboard.enable();
        return this;
    }
    disableKeyboard() {
        this.keyboard.disable();
        return this;
    }
    toggleKeyboard(enabled) {
        if (enabled != null) {
            if (enabled !== this.isKeyboardEnabled()) {
                if (enabled) {
                    this.enableKeyboard();
                }
                else {
                    this.disableKeyboard();
                }
            }
        }
        else {
            if (this.isKeyboardEnabled()) {
                this.disableKeyboard();
            }
            else {
                this.enableKeyboard();
            }
        }
        return this;
    }
    bindKey(keys, callback, action) {
        this.keyboard.on(keys, callback, action);
        return this;
    }
    unbindKey(keys, action) {
        this.keyboard.off(keys, action);
        return this;
    }
    // #endregion
    // #region mousewheel
    isMouseWheelEnabled() {
        return !this.mousewheel.disabled;
    }
    enableMouseWheel() {
        this.mousewheel.enable();
        return this;
    }
    disableMouseWheel() {
        this.mousewheel.disable();
        return this;
    }
    // #endregion
    // #region scroller
    lockScroller() {
        var _a;
        (_a = this.scroller.widget) === null || _a === void 0 ? void 0 : _a.lock();
    }
    unlockScroller() {
        var _a;
        (_a = this.scroller.widget) === null || _a === void 0 ? void 0 : _a.unlock();
    }
    updateScroller() {
        var _a;
        (_a = this.scroller.widget) === null || _a === void 0 ? void 0 : _a.update();
    }
    getScrollbarPosition() {
        const scroller = this.scroller.widget;
        return scroller.scrollbarPosition();
    }
    setScrollbarPosition(left, top, options) {
        const scroller = this.scroller.widget;
        scroller.scrollbarPosition(left, top, options);
        return this;
    }
    /**
     * Try to scroll to ensure that the position (x,y) on the graph (in local
     * coordinates) is at the center of the viewport. If only one of the
     * coordinates is specified, only scroll in the specified dimension and
     * keep the other coordinate unchanged.
     */
    scrollToPoint(x, y, options) {
        const scroller = this.scroller.widget;
        scroller.scrollToPoint(x, y, options);
        return this;
    }
    /**
     * Try to scroll to ensure that the center of graph content is at the
     * center of the viewport.
     */
    scrollToContent(options) {
        const scroller = this.scroller.widget;
        scroller.scrollToContent(options);
        return this;
    }
    /**
     * Try to scroll to ensure that the center of cell is at the center of
     * the viewport.
     */
    scrollToCell(cell, options) {
        const scroller = this.scroller.widget;
        scroller.scrollToCell(cell, options);
        return this;
    }
    /**
     * Position the center of graph to the center of the viewport.
     */
    center(optons) {
        return this.centerPoint(optons);
    }
    centerPoint(x, y, options) {
        const scroller = this.scroller.widget;
        scroller.centerPoint(x, y, options);
        return this;
    }
    centerContent(options) {
        const scroller = this.scroller.widget;
        scroller.centerContent(options);
        return this;
    }
    centerCell(cell, options) {
        const scroller = this.scroller.widget;
        scroller.centerCell(cell, options);
        return this;
    }
    positionContent(pos, options) {
        const scroller = this.scroller.widget;
        scroller.positionContent(pos, options);
        return this;
    }
    positionCell(cell, direction, options) {
        const scroller = this.scroller.widget;
        scroller.positionCell(cell, direction, options);
        return this;
    }
    positionRect(rect, direction, options) {
        const scroller = this.scroller.widget;
        scroller.positionRect(rect, direction, options);
        return this;
    }
    positionPoint(point, x, y, options = {}) {
        const scroller = this.scroller.widget;
        scroller.positionPoint(point, x, y, options);
        return this;
    }
    zoom(factor, options) {
        const scroller = this.scroller.widget;
        if (factor == null) {
            return scroller.zoom();
        }
        scroller.zoom(factor, options);
        return this;
    }
    zoomToRect(rect, options = {}) {
        const scroller = this.scroller.widget;
        scroller.zoomToRect(rect, options);
        return this;
    }
    zoomToFit(options = {}) {
        const scroller = this.scroller.widget;
        scroller.zoomToFit(options);
        return this;
    }
    transitionToPoint(x, y, options) {
        const scroller = this.scroller.widget;
        scroller.transitionToPoint(x, y, options);
        return this;
    }
    transitionToRect(rect, options = {}) {
        const scroller = this.scroller.widget;
        scroller.transitionToRect(rect, options);
        return this;
    }
    isPannable() {
        return this.scroller.pannable;
    }
    enablePanning() {
        this.scroller.enablePanning();
        return this;
    }
    disablePanning() {
        this.scroller.disablePanning();
        return this;
    }
    togglePanning(pannable) {
        if (pannable == null) {
            if (this.isPannable()) {
                this.disablePanning();
            }
            else {
                this.enablePanning();
            }
        }
        else {
            if (pannable !== this.isPannable()) {
                if (pannable) {
                    this.enablePanning();
                }
                else {
                    this.disablePanning();
                }
            }
        }
        return this;
    }
    // #endregion
    // #region selection
    isSelectionEnabled() {
        return !this.selection.disabled;
    }
    enableSelection() {
        this.selection.enable();
        return this;
    }
    disableSelection() {
        this.selection.disable();
        return this;
    }
    toggleSelection(enabled) {
        if (enabled != null) {
            if (enabled !== this.isSelectionEnabled()) {
                if (enabled) {
                    this.enableSelection();
                }
                else {
                    this.disableSelection();
                }
            }
        }
        else {
            if (this.isSelectionEnabled()) {
                this.disableSelection();
            }
            else {
                this.enableSelection();
            }
        }
        return this;
    }
    isMultipleSelection() {
        return this.selection.isMultiple();
    }
    enableMultipleSelection() {
        this.selection.enableMultiple();
        return this;
    }
    disableMultipleSelection() {
        this.selection.disableMultiple();
        return this;
    }
    toggleMultipleSelection(multiple) {
        if (multiple != null) {
            if (multiple !== this.isMultipleSelection()) {
                if (multiple) {
                    this.enableMultipleSelection();
                }
                else {
                    this.disableMultipleSelection();
                }
            }
        }
        else {
            if (this.isMultipleSelection()) {
                this.disableMultipleSelection();
            }
            else {
                this.enableMultipleSelection();
            }
        }
        return this;
    }
    isSelectionMovable() {
        return this.selection.widget.options.movable !== false;
    }
    enableSelectionMovable() {
        this.selection.widget.options.movable = true;
        return this;
    }
    disableSelectionMovable() {
        this.selection.widget.options.movable = false;
        return this;
    }
    toggleSelectionMovable(movable) {
        if (movable != null) {
            if (movable !== this.isSelectionMovable()) {
                if (movable) {
                    this.enableSelectionMovable();
                }
                else {
                    this.disableSelectionMovable();
                }
            }
        }
        else {
            if (this.isSelectionMovable()) {
                this.disableSelectionMovable();
            }
            else {
                this.enableSelectionMovable();
            }
        }
        return this;
    }
    isRubberbandEnabled() {
        return !this.selection.rubberbandDisabled;
    }
    enableRubberband() {
        this.selection.enableRubberband();
        return this;
    }
    disableRubberband() {
        this.selection.disableRubberband();
        return this;
    }
    toggleRubberband(enabled) {
        if (enabled != null) {
            if (enabled !== this.isRubberbandEnabled()) {
                if (enabled) {
                    this.enableRubberband();
                }
                else {
                    this.disableRubberband();
                }
            }
        }
        else {
            if (this.isRubberbandEnabled()) {
                this.disableRubberband();
            }
            else {
                this.enableRubberband();
            }
        }
        return this;
    }
    isStrictRubberband() {
        return this.selection.widget.options.strict === true;
    }
    enableStrictRubberband() {
        this.selection.widget.options.strict = true;
        return this;
    }
    disableStrictRubberband() {
        this.selection.widget.options.strict = false;
        return this;
    }
    toggleStrictRubberband(strict) {
        if (strict != null) {
            if (strict !== this.isStrictRubberband()) {
                if (strict) {
                    this.enableStrictRubberband();
                }
                else {
                    this.disableStrictRubberband();
                }
            }
        }
        else {
            if (this.isStrictRubberband()) {
                this.disableStrictRubberband();
            }
            else {
                this.enableStrictRubberband();
            }
        }
        return this;
    }
    setRubberbandModifiers(modifiers) {
        this.selection.setModifiers(modifiers);
    }
    setSelectionFilter(filter) {
        this.selection.setFilter(filter);
        return this;
    }
    setSelectionDisplayContent(content) {
        this.selection.setContent(content);
        return this;
    }
    isSelectionEmpty() {
        return this.selection.isEmpty();
    }
    cleanSelection() {
        this.selection.clean();
        return this;
    }
    getSelectedCells() {
        return this.selection.cells;
    }
    getSelectedCellCount() {
        return this.selection.length;
    }
    isSelected(cell) {
        return this.selection.isSelected(cell);
    }
    select(cells, options = {}) {
        this.selection.select(cells, options);
        return this;
    }
    unselect(cells, options = {}) {
        this.selection.unselect(cells, options);
        return this;
    }
    // #endregion
    // #region snapline
    isSnaplineEnabled() {
        return !this.snapline.widget.disabled;
    }
    enableSnapline() {
        this.snapline.widget.enable();
        return this;
    }
    disableSnapline() {
        this.snapline.widget.disable();
        return this;
    }
    toggleSnapline(enabled) {
        if (enabled != null) {
            if (enabled !== this.isSnaplineEnabled()) {
                if (enabled) {
                    this.enableSnapline();
                }
                else {
                    this.disableSnapline();
                }
            }
        }
        else {
            if (this.isSnaplineEnabled()) {
                this.disableSnapline();
            }
            else {
                this.enableSnapline();
            }
            return this;
        }
    }
    hideSnapline() {
        this.snapline.widget.hide();
        return this;
    }
    setSnaplineFilter(filter) {
        this.snapline.widget.setFilter(filter);
        return this;
    }
    isSnaplineOnResizingEnabled() {
        return this.snapline.widget.options.resizing === true;
    }
    enableSnaplineOnResizing() {
        this.snapline.widget.options.resizing = true;
        return this;
    }
    disableSnaplineOnResizing() {
        this.snapline.widget.options.resizing = false;
        return this;
    }
    toggleSnaplineOnResizing(enableOnResizing) {
        if (enableOnResizing != null) {
            if (enableOnResizing !== this.isSnaplineOnResizingEnabled()) {
                if (enableOnResizing) {
                    this.enableSnaplineOnResizing();
                }
                else {
                    this.disableSnaplineOnResizing();
                }
            }
        }
        else {
            if (this.isSnaplineOnResizingEnabled()) {
                this.disableSnaplineOnResizing();
            }
            else {
                this.enableSnaplineOnResizing();
            }
        }
        return this;
    }
    isSharpSnapline() {
        return this.snapline.widget.options.sharp === true;
    }
    enableSharpSnapline() {
        this.snapline.widget.options.sharp = true;
        return this;
    }
    disableSharpSnapline() {
        this.snapline.widget.options.sharp = false;
        return this;
    }
    toggleSharpSnapline(sharp) {
        if (sharp != null) {
            if (sharp !== this.isSharpSnapline()) {
                if (sharp) {
                    this.enableSharpSnapline();
                }
                else {
                    this.disableSharpSnapline();
                }
            }
        }
        else {
            if (this.isSharpSnapline()) {
                this.disableSharpSnapline();
            }
            else {
                this.enableSharpSnapline();
            }
        }
        return this;
    }
    getSnaplineTolerance() {
        return this.snapline.widget.options.tolerance;
    }
    setSnaplineTolerance(tolerance) {
        this.snapline.widget.options.tolerance = tolerance;
        return this;
    }
    // #endregion
    // #region tools
    removeTools() {
        this.emit('tools:remove');
        return this;
    }
    hideTools() {
        this.emit('tools:hide');
        return this;
    }
    showTools() {
        this.emit('tools:show');
        return this;
    }
    // #endregion
    // #region format
    toSVG(callback, options = {}) {
        this.format.toSVG(callback, options);
    }
    toDataURL(callback, options) {
        this.format.toDataURL(callback, options);
    }
    toPNG(callback, options = {}) {
        this.format.toPNG(callback, options);
    }
    toJPEG(callback, options = {}) {
        this.format.toJPEG(callback, options);
    }
    // #endregion
    // #region print
    printPreview(options) {
        this.print.show(options);
    }
    // #endregion
    // #region dispose
    dispose() {
        this.view.dispose();
        this.renderer.dispose();
    }
}
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "lockScroller", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "unlockScroller", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "updateScroller", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "getScrollbarPosition", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "setScrollbarPosition", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "scrollToPoint", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "scrollToContent", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "scrollToCell", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "centerPoint", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "centerContent", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "centerCell", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "positionContent", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "positionCell", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "positionRect", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "positionPoint", null);
__decorate([
    Decorator.checkScroller()
], Graph.prototype, "zoom", null);
__decorate([
    Basecoat.dispose()
], Graph.prototype, "dispose", null);
(function (Graph) {
    Graph.View = GraphView;
    Graph.Hook = HookManager;
    Graph.Renderer = ViewRenderer;
    Graph.Keyboard = Shortcut;
    Graph.MouseWheel = Wheel;
    Graph.BaseManager = Base;
    Graph.DefsManager = Defs;
    Graph.GridManager = Grid;
    Graph.CoordManager = Coord;
    Graph.PrintManager = Print;
    Graph.FormatManager = Format;
    Graph.MiniMapManager = MiniMap;
    Graph.HistoryManager = History;
    Graph.SnaplineManager = Snapline;
    Graph.ScrollerManager = Scroller;
    Graph.ClipboardManager = Clipboard;
    Graph.TransformManager = Transform;
    Graph.HighlightManager = Highlight;
    Graph.BackgroundManager = Background;
    Graph.SelectionManager = Selection;
})(Graph || (Graph = {}));
(function (Graph) {
    function render(options, data) {
        const graph = options instanceof HTMLElement
            ? new Graph({ container: options })
            : new Graph(options);
        if (data != null) {
            graph.fromJSON(data);
        }
        return graph;
    }
    Graph.render = render;
})(Graph || (Graph = {}));
(function (Graph) {
    Graph.registerNode = Node.registry.register;
    Graph.registerEdge = Edge.registry.register;
    Graph.registerView = CellView.registry.register;
    Graph.registerAttr = AttrDefinition.registry.register;
    Graph.registerGrid = GridDefinition.registry.register;
    Graph.registerFilter = FilterDefinition.registry.register;
    Graph.registerNodeTool = NodeTool.registry.register;
    Graph.registerEdgeTool = EdgeTool.registry.register;
    Graph.registerBackground = BackgroundDefinition.registry.register;
    Graph.registerHighlighter = HighlightDefinition.registry.register;
    Graph.registerPortLayout = PortLayout.registry.register;
    Graph.registerPortLabelLayout = PortLabelLayout.registry.register;
    Graph.registerMarker = Marker.registry.register;
    Graph.registerRouter = Router.registry.register;
    Graph.registerConnector = Connector.registry.register;
    Graph.registerNodeConnectionAnchor = NodeConnectionAnchor.registry.register;
    Graph.registerEdgeConnectionAnchor = EdgeConnectionAnchor.registry.register;
    Graph.registerConnectionPoint = ConnectionPoint.registry.register;
    Graph.registerConnectionStrategy = ConnectionStrategy.registry.register;
    Graph.registerHTMLComponent = HTML.componentRegistry.register;
})(Graph || (Graph = {}));
(function (Graph) {
    Graph.unregisterNode = Node.registry.unregister;
    Graph.unregisterEdge = Edge.registry.unregister;
    Graph.unregisterView = CellView.registry.unregister;
    Graph.unregisterAttr = AttrDefinition.registry.unregister;
    Graph.unregisterGrid = GridDefinition.registry.unregister;
    Graph.unregisterFilter = FilterDefinition.registry.unregister;
    Graph.unregisterNodeTool = NodeTool.registry.unregister;
    Graph.unregisterEdgeTool = EdgeTool.registry.unregister;
    Graph.unregisterBackground = BackgroundDefinition.registry.unregister;
    Graph.unregisterHighlighter = HighlightDefinition.registry.unregister;
    Graph.unregisterPortLayout = PortLayout.registry.unregister;
    Graph.unregisterPortLabelLayout = PortLabelLayout.registry.unregister;
    Graph.unregisterMarker = Marker.registry.unregister;
    Graph.unregisterRouter = Router.registry.unregister;
    Graph.unregisterConnector = Connector.registry.unregister;
    Graph.unregisterNodeConnectionAnchor = NodeConnectionAnchor.registry.unregister;
    Graph.unregisterEdgeConnectionAnchor = EdgeConnectionAnchor.registry.unregister;
    Graph.unregisterConnectionPoint = ConnectionPoint.registry.unregister;
    Graph.unregisterConnectionStrategy = ConnectionStrategy.registry.unregister;
    Graph.unregisterHTMLComponent = HTML.componentRegistry.unregister;
})(Graph || (Graph = {}));
//# sourceMappingURL=graph.js.map